import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    PageHeader,
    FlexRowBetween,
    FlexRow,
    AppTable,
    PaginationComponent,
} from 'components'
import {
    AppPrimaryButton,
    AppSecondaryButton,
    LinkText,
} from 'elements'
import { DesktopMode, MobileMode, useTabletAndBelowMediaQuery } from 'layout'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'
import { useNotification } from 'hooks/toast'
import { Input, Form, Modal } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { CaretLeft, Pencil, Play } from 'phosphor-react'
import styled from 'styled-components'

const PageWrap = styled.div`
    padding: 20px;
    margin-top: 10px;
`

const SectionBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    flex-wrap: wrap;
    gap: 8px;
`

const TableTitle = styled.span`
    font-weight: 600;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.black};
`

const PAGE_SIZE = 5

const FREQUENCY_OPTIONS = [
    { key: 'q', text: 'Quarterly', value: 'Quarterly' },
    { key: 'm', text: 'Monthly', value: 'Monthly' },
    { key: 'w', text: 'Weekly', value: 'Weekly' },
    { key: 'a', text: 'Annual', value: 'Annual' },
]

const TEMPLATE_OPTIONS = [
    { key: 't1', text: 'Template A', value: 'Template A' },
    { key: 't2', text: 'Template B', value: 'Template B' },
    { key: 't3', text: 'Template C', value: 'Template C' },
]

const createMockSchedules = () => [
    {
        Id: '1',
        assetName: 'Asset Acre 1',
        template: 'Template A',
        frequency: 'Quarterly',
        lastInspection: 'Apr. 18',
        nextInspection: 'Jan. 5',
    },
    {
        Id: '2',
        assetName: 'Asset Bear 1',
        template: 'Template B',
        frequency: 'Monthly',
        lastInspection: 'Feb. 2021',
        nextInspection: 'Jun. 13',
    },
    {
        Id: '3',
        assetName: 'Asset-Ayr-1',
        template: 'Template A',
        frequency: 'Weekly',
        lastInspection: 'Mar. 1',
        nextInspection: 'Mar. 8',
    },
    {
        Id: '4',
        assetName: 'Asset-Ayr-2',
        template: 'Template C',
        frequency: 'Annual',
        lastInspection: '—',
        nextInspection: 'Dec. 31',
    },
    {
        Id: '5',
        assetName: 'Asset Creek 2',
        template: 'Template B',
        frequency: 'Quarterly',
        lastInspection: 'Jan. 10',
        nextInspection: 'Apr. 10',
    },
    {
        Id: '6',
        assetName: 'Asset Delta 3',
        template: 'Template A',
        frequency: 'Monthly',
        lastInspection: 'Nov. 2',
        nextInspection: 'Dec. 2',
    },
    {
        Id: '7',
        assetName: 'Asset Echo 4',
        template: 'Template C',
        frequency: 'Monthly',
        lastInspection: 'Oct. 15',
        nextInspection: 'Nov. 15',
    },
]

const emptyForm = {
    assetName: '',
    template: '',
    frequency: '',
}

export const ScheduleInspectionsScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)
    const { successToast, warningToast } = useNotification()
    const isMobile = useTabletAndBelowMediaQuery()

    const [schedules, setSchedules] = useState(createMockSchedules)
    const [searchText, setSearchText] = useState('')
    const [filter, setFilter] = useState({
        PageNumber: 1,
        PageSize: PAGE_SIZE,
    })
    const [form, setForm] = useState(emptyForm)
    const [editingId, setEditingId] = useState(null)
    const [assignModalOpen, setAssignModalOpen] = useState(false)

    const canView = permissions.canViewAssetModule
    const canEdit = permissions.canScheduleInspection && !permissions.isViewOnly

    useEffect(() => {
        if (!canView) {
            navigate('/app/asset-management', { replace: true })
        }
    }, [canView, navigate])

    const filtered = useMemo(() => {
        if (!searchText.trim()) return schedules
        const q = searchText.trim().toLowerCase()
        return schedules.filter(
            row =>
                row.assetName.toLowerCase().includes(q) ||
                row.template.toLowerCase().includes(q),
        )
    }, [schedules, searchText])

    const totalCount = filtered.length
    const paginated = useMemo(() => {
        const start = (filter.PageNumber - 1) * filter.PageSize
        return filtered.slice(start, start + filter.PageSize)
    }, [filtered, filter.PageNumber, filter.PageSize])

    const pageChanged = useCallback(activePage => {
        setFilter(prev => ({ ...prev, PageNumber: activePage }))
    }, [])

    const resetFilters = () => {
        setSearchText('')
        setFilter(prev => ({ ...prev, PageNumber: 1 }))
        warningToast('Filters reset.')
    }

    const openAssign = () => {
        if (!canEdit) return
        setEditingId(null)
        setForm(emptyForm)
        setAssignModalOpen(true)
    }

    const fillFormFromRow = useCallback(
        row => {
            if (!canEdit) return
            setEditingId(row.Id)
            setForm({
                assetName: row.assetName,
                template: row.template,
                frequency: row.frequency,
            })
            setAssignModalOpen(true)
        },
        [canEdit],
    )

    const handleSave = () => {
        if (!canEdit) return
        if (!form.assetName?.trim() || !form.template || !form.frequency) {
            warningToast('Enter asset, template, and frequency.')
            return
        }

        if (editingId) {
            setSchedules(prev =>
                prev.map(s =>
                    s.Id === editingId
                        ? {
                            ...s,
                            assetName: form.assetName.trim(),
                            template: form.template,
                            frequency: form.frequency,
                        }
                        : s,
                ),
            )
            successToast('Schedule updated locally (API pending).')
        } else {
            const newRow = {
                Id: `mock-${Date.now()}`,
                assetName: form.assetName.trim(),
                template: form.template,
                frequency: form.frequency,
                lastInspection: '—',
                nextInspection: 'TBD',
            }
            setSchedules(prev => [...prev, newRow])
            successToast('Schedule saved locally (API pending).')
        }

        setForm(emptyForm)
        setEditingId(null)
        setAssignModalOpen(false)
    }

    const handleCancel = () => {
        setForm(emptyForm)
        setEditingId(null)
        setAssignModalOpen(false)
    }

    const handleStart = useCallback(
        row => {
            if (!canEdit) return
            successToast(
                `Inspection start logged for "${row.assetName}" (mock). API pending.`,
            )
        },
        [canEdit, successToast],
    )

    const renderFormFields = () => (
        <Form>
            <Form.Field>
                <label>Asset</label>
                <Input
                    placeholder="Asset name"
                    value={form.assetName}
                    onChange={e =>
                        setForm(f => ({ ...f, assetName: e.target.value }))
                    }
                    disabled={!canEdit}
                />
            </Form.Field>
            <Form.Field>
                <label>Template</label>
                <Dropdown
                    selection
                    fluid
                    placeholder="Select template"
                    options={TEMPLATE_OPTIONS}
                    value={form.template}
                    onChange={(_, { value }) =>
                        setForm(f => ({ ...f, template: value || '' }))
                    }
                    disabled={!canEdit}
                />
            </Form.Field>
            <Form.Field>
                <label>Frequency</label>
                <Dropdown
                    selection
                    fluid
                    placeholder="Select frequency"
                    options={FREQUENCY_OPTIONS}
                    value={form.frequency}
                    onChange={(_, { value }) =>
                        setForm(f => ({ ...f, frequency: value || '' }))
                    }
                    disabled={!canEdit}
                />
            </Form.Field>
            <FlexRow className="gap-2 mt-3 flex-wrap">
                <AppPrimaryButton
                    type="button"
                    onClick={handleSave}
                    disabled={!canEdit}
                >
                    Save
                </AppPrimaryButton>
                <AppSecondaryButton type="button" onClick={handleCancel}>
                    Cancel
                </AppSecondaryButton>
            </FlexRow>
        </Form>
    )

    const tableColumns = useMemo(
        () => [
            { title: 'Asset Name', key: 'assetName', textAlign: 'left' },
            { title: 'Template', key: 'template', textAlign: 'left' },
            { title: 'Frequency', key: 'frequency', textAlign: 'left' },
            {
                title: 'Last Inspection',
                key: 'lastInspection',
                textAlign: 'left',
            },
            {
                title: 'Next Inspection',
                key: 'nextInspection',
                textAlign: 'left',
            },
            {
                title: 'Actions',
                key: 'actions',
                textAlign: 'right',
                render: item => (
                    <FlexRow className="justify-content-end gap-2 flex-wrap">
                        <AppSecondaryButton
                            type="button"
                            title="Start inspection"
                            disabled={!canEdit}
                            onClick={() => handleStart(item)}
                        >
                            <FlexRow className="align-items-center gap-1">
                                <Play size={16} weight="bold" />
                                Start
                            </FlexRow>
                        </AppSecondaryButton>
                        <button
                            type="button"
                            className="ui icon button basic"
                            title="Edit"
                            disabled={!canEdit}
                            onClick={() => fillFormFromRow(item)}
                        >
                            <Pencil size={18} />
                        </button>
                    </FlexRow>
                ),
            },
        ],
        [canEdit, handleStart, fillFormFromRow],
    )

    if (!canView) {
        return null
    }

    return (
        <PageWrap>
            <FlexRow
                className="align-items-center mb-3 pointer"
                onClick={() => navigate('/app/asset-management')}
            >
                <CaretLeft size={22} />
                <span className="ms-2">Back to Assets</span>
            </FlexRow>

            <DesktopMode>
                <FlexRowBetween className="mb-3 align-items-end flex-wrap gap-2">
                    <PageHeader header="Schedule Inspections" />
                    <AppPrimaryButton
                        type="button"
                        onClick={openAssign}
                        disabled={!canEdit}
                    >
                        + Assign inspection
                    </AppPrimaryButton>
                </FlexRowBetween>
            </DesktopMode>

            <MobileMode>
                <PageHeader header="Schedule Inspections" />
                <AppPrimaryButton
                    type="button"
                    fluid
                    className="mb-3"
                    onClick={openAssign}
                    disabled={!canEdit}
                >
                    + Assign inspection
                </AppPrimaryButton>
            </MobileMode>

            <div className="col-12">
                <Input
                    fluid
                    icon="search"
                    iconPosition="left"
                    placeholder={
                        isMobile
                            ? 'Search or select asset...'
                            : 'Search by asset name...'
                    }
                    value={searchText}
                    onChange={e => {
                        setSearchText(e.target.value)
                        setFilter(f => ({ ...f, PageNumber: 1 }))
                    }}
                    className="mb-3"
                />

                <SectionBar>
                    <TableTitle>Scheduled inspections</TableTitle>
                    <LinkText onClick={resetFilters}>Reset all</LinkText>
                </SectionBar>

                <AppTable
                    columns={tableColumns}
                    data={paginated}
                    hidePagination
                    emptyMessage="No scheduled inspections match your search."
                />

                <div className="w-100 d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                        {totalCount === 0
                            ? '0 results'
                            : `${(filter.PageNumber - 1) * filter.PageSize + 1} – ${Math.min(
                                filter.PageNumber * filter.PageSize,
                                totalCount,
                            )} of ${totalCount}`}
                    </span>
                    <PaginationComponent
                        activePage={filter.PageNumber}
                        pageChanged={pageChanged}
                        pageSize={filter.PageSize}
                        totalCount={totalCount}
                    />
                </div>
            </div>

            <Modal
                open={assignModalOpen}
                onClose={handleCancel}
                size="small"
                closeIcon
            >
                <Modal.Header>
                    {editingId ? 'Edit schedule' : 'Schedule inspection'}
                </Modal.Header>
                <Modal.Content scrolling>
                    {renderFormFields()}
                </Modal.Content>
            </Modal>
        </PageWrap>
    )
}
